import { hasStoreCatalog, isConnectedRozetkaStore } from './store-catalog-access'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/lib/api-client'
import { useOrgStore } from '@/lib/store'

interface Marketplace {
  name?: string
  slug: string
}

export interface ConnectedStore {
  id: number
  name: string
  merchant_id: string
  url: string
  validation_status: string
  seller_identity_status: string
  has_public_seller_identity: boolean
  found_products_count: number
  active_products_count: number
  inactive_products_count: number
  unknown_products_count: number
  marketplace: Marketplace
}

export interface KmpEligibility {
  is_member: boolean
  display_name?: string
  store?: {
    id: number
    name: string
    merchant_id: string
    url: string
  }
}

export function marketplaceLabel(marketplace: Marketplace | null | undefined) {
  const name = marketplace?.name?.trim()
  if (name) return name
  const slug = marketplace?.slug?.trim()
  if (!slug) return 'Marketplace'
  if (slug === 'rozetka') return 'Rozetka'
  if (slug === 'prom') return 'Prom'
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function useConnectedRozetkaStore(enabled = true) {
  const activeOrgId = useOrgStore((state) => state.activeOrgId)

  return useQuery<ConnectedStore | null>({
    queryKey: ['tracker', 'connected-rozetka-store', activeOrgId],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/tracker/stores/')
      const stores = (response.data.results || response.data || []) as ConnectedStore[]
      const rozetkaStores = stores.filter(
        (store) => store.marketplace?.slug === 'rozetka' && hasStoreCatalog(store)
      )

      return (
        rozetkaStores.find((store) => store.validation_status === 'valid') ||
        rozetkaStores[0] ||
        null
      )
    },
    enabled: enabled && Boolean(activeOrgId),
    staleTime: 60_000,
  })
}

export function useRozetkaStores(enabled = true) {
  const activeOrgId = useOrgStore((state) => state.activeOrgId)

  return useQuery<ConnectedStore[]>({
    queryKey: ['tracker', 'rozetka-stores', activeOrgId],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/tracker/stores/')
      const stores = (response.data.results || response.data || []) as ConnectedStore[]
      return stores.filter(isConnectedRozetkaStore)
    },
    enabled: enabled && Boolean(activeOrgId),
    staleTime: 60_000,
  })
}

// Marketplaces whose connected stores surface owned products on the Products page.
export const OWNED_STORE_MARKETPLACES = ['rozetka', 'prom'] as const

export function useOwnedStores(enabled = true) {
  const activeOrgId = useOrgStore((state) => state.activeOrgId)

  return useQuery<ConnectedStore[]>({
    queryKey: ['tracker', 'owned-stores', activeOrgId],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/tracker/stores/')
      const stores = (response.data.results || response.data || []) as ConnectedStore[]
      return stores.filter(
        (store) =>
          OWNED_STORE_MARKETPLACES.includes(store.marketplace?.slug as (typeof OWNED_STORE_MARKETPLACES)[number]) &&
          hasStoreCatalog(store),
      )
    },
    enabled: enabled && Boolean(activeOrgId),
    staleTime: 60_000,
  })
}

export function useKmpEligibility(enabled = true) {
  const activeOrgId = useOrgStore((state) => state.activeOrgId)

  return useQuery<KmpEligibility>({
    queryKey: ['tracker', 'king-of-marketplaces', 'eligibility', activeOrgId],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/tracker/king-of-marketplaces/eligibility/')
      return response.data as KmpEligibility
    },
    enabled: enabled && Boolean(activeOrgId),
    staleTime: 60_000,
  })
}
